'use client';

/**
 * DevExtreme Locale & License Provider (app-level)
 *
 * Bridges next-intl locale with DevExtreme's internal locale system.
 * Lives at app level (not in @axon/grid-devextreme) to avoid duplicate
 * next-intl instances when grid package is transpiled by Next.js bundler.
 *
 * 1. Syncs DevExtreme's internal locale with next-intl locale.
 * 2. Loads Turkish message dictionary for DX UI elements (pager, filters, etc.).
 * 3. Loads custom Turkish overrides for 47 untranslated DataGrid/Pager keys.
 * 4. Configures DevExtreme license key from environment variable.
 *
 * Must be rendered inside NextIntlClientProvider so useLocale() works.
 * License: Set NEXT_PUBLIC_DX_LICENSE_KEY in .env to suppress trial banners.
 */

import React, { useEffect, useRef } from 'react';
import { useLocale } from 'next-intl';
import { locale as dxLocale, loadMessages } from 'devextreme/localization';
import { config } from 'devextreme/common';
import trMessages from 'devextreme/localization/messages/tr.json';
import { dxTrOverrides } from '@axon/grid-devextreme/locale';

// #907: detect the active locale at module init so DevExtreme picks
// the right language BEFORE any grid mounts. Reading from the URL
// path (browser only) avoids the SSR/hydration race that pre-fix let
// `loadMessages(trMessages)` populate the dictionary unconditionally
// and `dxLocale('en')` lag inside a useEffect — grids briefly
// rendered Turkish strings on /en/* routes.
//
// SSR pass picks 'en' as the safe default (DevExtreme's built-in
// dictionary). The hydration pass re-evaluates inside the provider's
// useEffect below, which catches locale changes during the session.
const initialLocale: 'en' | 'tr' = (() => {
  if (typeof window === 'undefined') return 'en';
  const path = window.location.pathname;
  return path.startsWith('/tr/') || path === '/tr' ? 'tr' : 'en';
})();

// Load Turkish messages ONLY when the active locale needs them.
// DevExtreme's default catalog is English; skipping the Turkish load
// on /en/* keeps the dictionary monolingual and the resolution
// deterministic (no language mixing during hydration).
if (initialLocale === 'tr') {
  loadMessages(trMessages);
  // Override untranslated keys with Turkish translations
  // (DRY: shared via @axon/grid-devextreme)
  loadMessages(dxTrOverrides);
}

// Set DevExtreme's internal locale synchronously at module init so
// any grid that renders before the provider's useEffect (e.g. inside
// a server component's hydration pass) reads the correct language.
dxLocale(initialLocale);

// Configure license key once at module level
const licenseKey =
  typeof window !== 'undefined' ? process.env.NEXT_PUBLIC_DX_LICENSE_KEY : undefined;

if (licenseKey) {
  config({ licenseKey });
}

interface DxLocaleProviderProps {
  children: React.ReactNode;
}

export function DxLocaleProvider({ children }: DxLocaleProviderProps) {
  const currentLocale = useLocale();
  const prevLocaleRef = useRef(currentLocale);

  useEffect(() => {
    if (prevLocaleRef.current === currentLocale) return;
    // #907: runtime locale switch — load the Turkish dictionary lazily
    // when the user navigates from /en/* to /tr/* mid-session. Module
    // init already handled the initial-load case; this catches the
    // transition without re-running the load on every grid mount.
    if (currentLocale === 'tr') {
      loadMessages(trMessages);
      loadMessages(dxTrOverrides);
    }
    prevLocaleRef.current = currentLocale;
    dxLocale(currentLocale);
  }, [currentLocale]);

  return <>{children}</>;
}
